import { useCallback, useEffect, useRef, useState } from "react";

/* The app's action button: it owns the pending / success / error feedback for a
   write, so no screen has to hand-roll an `isLoading` flag and a spinner again.

   WHY IT REPLACES A PLAIN <button>
   Every mutation in this app was a bare `onClick={handler}` that fired a
   `postApiCall` and forgot about it. Nothing told the user the click landed, so
   a slow save reads as a dead button and gets clicked twice - which is a real
   double-create, not a cosmetic bug. This component takes the promise, locks
   itself while it is in flight, and reports how it ended.

   THE ACTION CONTRACT
     onAction() resolves            -> success face, then back to idle
     onAction() rejects / throws    -> error face (+ onError), then idle
     onAction() returns `false`     -> nothing happened; straight back to idle
   The third case is the form-validation path: `validateXFields()` returns
   `false` when it put messages under the fields, and the button must not
   celebrate that. Any other return value counts as success.

   STYLING
   The visual identity stays with the caller's class - pass `create-button`
   and it is still the app's primary button, `create-button1 btn-info` and it
   is still the quiet one, `app-dialog-btn app-dialog-btn-primary` and it is
   a dialog action, and the context rules in theme.css
   (`.card-input button.create-button`, `.app-dialog-btn`, ...) keep applying.
   This component only adds the `.lb-*` layer: the stacked faces, the spinner
   and the success/error fills.

   The faces are stacked in one grid cell, so the button is as wide as its
   WIDEST face and never resizes mid-transition - a row of buttons does not
   reflow when one of them finishes. */

const DEFAULT_RESET_AFTER = 1400;

/**
 * The state machine on its own, for the few mutations whose trigger is not a
 * button (a <form onSubmit>, an icon in a table row).
 *
 * `status` is what to paint; `run` is what to call. A second `run` while one is
 * in flight is ignored, and a run that is superseded (or unmounted) can never
 * write state afterwards - that is what `runId`/`alive` guard.
 */
export const useAsyncAction = ({
  action,
  resetAfter = DEFAULT_RESET_AFTER,
  onError,
}) => {
  const [status, setStatus] = useState("idle");

  /* `status` is for rendering and `phase` is for deciding, because the guard in
     `run` has to see the CURRENT phase, not the one from the render that closed
     over it - two fast clicks share a render. */
  const phase = useRef("idle");
  const runId = useRef(0);
  const timer = useRef(null);
  const alive = useRef(true);

  const act = useRef(action);
  const fail = useRef(onError);

  /* Deliberately dependency-free: refresh the callbacks every render so an
     inline arrow in the caller does not re-create `run` and does not leave a
     stale closure behind either. */
  useEffect(() => {
    act.current = action;
    fail.current = onError;
  });

  const clear = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  const reset = useCallback(() => {
    runId.current += 1;
    clear();
    phase.current = "idle";
    setStatus("idle");
  }, [clear]);

  const run = useCallback(() => {
    if (phase.current === "pending") return;

    clear();
    const id = ++runId.current;
    phase.current = "pending";
    setStatus("pending");

    const settle = (next) => {
      if (!alive.current || id !== runId.current) return;
      clear();
      phase.current = next;
      setStatus(next);
      if (next === "idle") return;
      timer.current = setTimeout(() => {
        if (!alive.current || id !== runId.current) return;
        phase.current = "idle";
        setStatus("idle");
      }, resetAfter);
    };

    /* Promise.resolve().then(...) so a synchronous throw inside the action is a
       rejection like any other, and the pending face always paints for at
       least one frame even when the action is synchronous. */
    Promise.resolve()
      .then(() => act.current())
      .then(
        (result) => settle(result === false ? "idle" : "success"),
        (error) => {
          if (fail.current) fail.current(error);
          settle("error");
        }
      );
  }, [clear, resetAfter]);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      clear();
    };
  }, [clear]);

  return { status, run, reset, pending: status === "pending" };
};

const Spinner = () => (
  <svg
    className="lb-ico lb-spin"
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    aria-hidden="true"
  >
    <circle
      cx="6"
      cy="6"
      r="4.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeOpacity="0.22"
    />
    <path
      d="M10.5 6A4.5 4.5 0 0 0 6 1.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

const CheckMark = () => (
  <svg
    className="lb-ico"
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M2.6 6.3 4.9 8.6 9.4 3.6"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const AlertMark = () => (
  <svg
    className="lb-ico"
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    aria-hidden="true"
  >
    <path d="M6 2.9v3.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    <path d="M6 9.05h.01" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
  </svg>
);

/**
 * @param onAction      the write. May return a promise; `false` means "aborted".
 * @param children      the idle face - a label string, or an icon node.
 * @param pendingLabel  defaults to the idle label, so most buttons just spin.
 * @param iconOnly      faces carry the glyph only; the labels still feed ARIA.
 *                      For the trash icons in table rows, which have no room
 *                      for the word "Deleted" and must not change width.
 * @param ariaLabel     the accessible name when `children` is not a string.
 * @param resetAfter    ms the success/error face stays before returning to idle.
 * @param className     the caller's own button classes (e.g. `create-button`,
 *                      or `lb-bare` for an icon that must not look like one).
 */
const LoadingButton = ({
  onAction,
  children,
  pendingLabel,
  successLabel = "Done",
  errorLabel = "Try again",
  resetAfter = DEFAULT_RESET_AFTER,
  disabled = false,
  iconOnly = false,
  ariaLabel,
  onError,
  onClick,
  type = "button",
  title,
  id,
  className = "",
  ...rest
}) => {
  const { status, run, pending } = useAsyncAction({
    action: onAction,
    resetAfter,
    onError,
  });

  /* The idle face is the caller's own content - keeping it verbatim is what
     lets an icon trigger stay an icon. The other three are this component's. */
  const faces = [
    { key: "idle", body: children, text: children },
    {
      key: "pending",
      body: (
        <>
          <Spinner />
          {iconOnly ? null : pendingLabel ?? children}
        </>
      ),
      text: pendingLabel ?? children,
    },
    {
      key: "success",
      body: (
        <>
          <CheckMark />
          {iconOnly ? null : successLabel}
        </>
      ),
      text: successLabel,
    },
    {
      key: "error",
      body: (
        <>
          <AlertMark />
          {iconOnly ? null : errorLabel}
        </>
      ),
      text: errorLabel,
    },
  ];

  const label = (faces.find((face) => face.key === status) || faces[0]).text;
  const name = ariaLabel ?? (typeof label === "string" ? label : undefined);

  return (
    <>
      <button
        type={type}
        id={id}
        title={title}
        disabled={disabled}
        aria-label={name}
        aria-busy={pending || undefined}
        aria-disabled={pending || undefined}
        onClick={(event) => {
          /* Pending is not `disabled`: a disabled button loses focus and drops
             out of the tab order mid-save, which moves the keyboard user. It
             stays focusable and swallows the click instead. */
          if (pending) {
            event.preventDefault();
            return;
          }
          if (onClick) onClick(event);
          run();
        }}
        className={`lb-btn ${className} ${status === "idle" ? "" : `is-${status}`}`.trim()}
        {...rest}
      >
        {/* aria-hidden because the faces are four copies of the same label and
            only `aria-label` above should be read. */}
        <span className="lb-faces" aria-hidden="true">
          {faces.map((face) => (
            <span
              key={face.key}
              className={`lb-face${face.key === status ? " is-on" : ""}`}
            >
              {face.body}
            </span>
          ))}
        </span>
      </button>

      {/* The outcome, announced once - the faces themselves are hidden. */}
      <span role="status" aria-live="polite" className="lb-sr">
        {status === "success" ? successLabel : status === "error" ? errorLabel : ""}
      </span>
    </>
  );
};

export default LoadingButton;
